import { IconCheck, IconChevDown, IconLogout } from '@ui/icons'
import Sheet from '@ui/Sheet'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AccountTypeEnum,
  useMyAccountsQuery,
  useSelectAccountMutation,
} from '@/__generated__/hooks'
import { useCurrentAccount } from '@/contexts/CurrentAccountProvider'
import { useCurrentUser } from '@/contexts/CurrentUserProvider'

// Switching account full-reloads — the JWT cookie is account-scoped and the
// Apollo cache would carry stale data across tenants otherwise.
type Props = {
  variant?: 'dark' | 'light'
}

const AccountSwitcher = ({ variant = 'dark' }: Props) => {
  const { t } = useTranslation()
  const user = useCurrentUser()
  const currentAccount = useCurrentAccount()
  const { data, loading } = useMyAccountsQuery()
  const [selectAccount, { loading: selecting }] = useSelectAccountMutation()
  const [open, setOpen] = useState(false)

  const memberships = data?.myAccounts ?? []
  const hasMultiple = memberships.length > 1

  const handleSwitch = async (accountId: string) => {
    if (selecting) return
    if (accountId === currentAccount?.id) {
      setOpen(false)
      return
    }
    const { data: res } = await selectAccount({ variables: { accountId } })
    if (res?.selectAccount.success) {
      window.location.assign('/')
    }
  }

  const handleLogout = () => {
    window.location.assign('/auth/logout')
  }

  const initial =
    currentAccount?.name?.charAt(0).toUpperCase() ??
    user.email?.charAt(0).toUpperCase() ??
    user.name.charAt(0).toUpperCase()

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`
          flex items-center gap-3 w-full text-left
          rounded-xl px-3 py-2 transition
          ${
            variant === 'dark'
              ? 'bg-white/5 border border-white/10 hover:bg-white/10'
              : 'bg-white/10 hover:bg-white/15'
          }
        `}
        aria-label={t('account-switcher.switch')}
      >
        <span
          className={`
            w-7 h-7 rounded-lg grid place-items-center font-bold text-sm
            ${
              variant === 'dark'
                ? 'bg-green-600 text-white'
                : 'bg-flag text-white'
            }
          `}
          aria-hidden="true"
        >
          {initial}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-white truncate">
            {currentAccount?.name ?? user.name}
          </span>
          <span className="block text-[11px] text-green-100/80 truncate">
            {currentAccount
              ? t(
                  `account-switcher.type-${currentAccount.type.toLowerCase()}` as const
                )
              : user.email}
          </span>
        </span>
        {hasMultiple && (
          <IconChevDown
            size={14}
            className={
              variant === 'dark' ? 'text-green-100/70' : 'text-white/70'
            }
          />
        )}
      </button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={t('account-switcher.switch')}
      >
        <p className="text-sm text-ink-500 mb-3">
          {t('account-switcher.signed-in-as', { name: user.name })}
        </p>
        {loading && (
          <p className="text-sm text-ink-500">{t('common.loading')}</p>
        )}
        <ul className="flex flex-col gap-1 mb-4">
          {memberships.map((m) => {
            const active = m.account.id === currentAccount?.id
            return (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => handleSwitch(m.account.id)}
                  disabled={selecting}
                  className={`
                    w-full text-left flex items-center gap-3 px-3 py-3 rounded-lg
                    transition
                    ${
                      active
                        ? 'bg-green-50 border-l-[3px] border-green-700'
                        : 'hover:bg-ink-100/40'
                    }
                  `}
                >
                  <span className="w-8 h-8 grid place-items-center rounded-lg bg-green-100 text-green-800 font-bold text-sm">
                    {m.account.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-ink-900 truncate">
                      {m.account.name}
                    </span>
                    <span className="block text-xs text-ink-500">
                      {t(
                        `account-switcher.type-${m.account.type.toLowerCase()}` as const
                      )}
                    </span>
                  </span>
                  {active && <IconCheck size={16} className="text-green-700" />}
                </button>
              </li>
            )
          })}
        </ul>
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100/60 rounded-lg w-full"
        >
          <IconLogout size={16} />
          {t('account-switcher.log-out')}
        </button>
      </Sheet>
    </>
  )
}

export { AccountTypeEnum }

export default AccountSwitcher
